/**
 * Demo data for PropAlgos landing experience.
 *
 * Provides realistic demo strategies to showcase platform capabilities
 * before users spin up compute. All content here renders under explicit
 * demo labeling — never as user-specific state.
 */

import type { TearSheetData } from '../components/tearsheet/TearSheet';

// Seeded random for reproducible data
class SeededRandom {
  private seed: number;

  constructor(seed: number) {
    this.seed = seed;
  }

  next(): number {
    this.seed = (this.seed * 1103515245 + 12345) & 0x7fffffff;
    return this.seed / 0x7fffffff;
  }

  // Normal distribution via Box-Muller transform
  nextGaussian(): number {
    const u1 = this.next();
    const u2 = this.next();
    return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  }

  // Student's t distribution (fat tails)
  nextStudentT(df: number): number {
    const u = this.nextGaussian();
    const v = this.nextChiSquared(df);
    return u / Math.sqrt(v / df);
  }

  nextChiSquared(df: number): number {
    let sum = 0;
    for (let i = 0; i < df; i++) {
      const z = this.nextGaussian();
      sum += z * z;
    }
    return sum;
  }
}

interface ReturnGeneratorConfig {
  days: number;
  annualReturn: number;
  annualVol: number;
  drawdownIntensity: number;
  seed: number;
}

/**
 * Generate realistic daily returns with:
 * - Fat tails (Student-t distribution)
 * - Volatility clustering (GARCH-like)
 * - Mean reversion
 */
function generateDailyReturns(config: ReturnGeneratorConfig): number[] {
  const { days, annualReturn, annualVol, drawdownIntensity, seed } = config;
  const rng = new SeededRandom(seed);

  const dailyMean = annualReturn / 252;
  const dailyVol = annualVol / Math.sqrt(252);

  const returns: number[] = [];
  let volatility = dailyVol;
  let cumReturn = 1;

  for (let i = 0; i < days; i++) {
    // Volatility clustering (mean-reverting)
    const volShock = rng.nextGaussian() * 0.05;
    volatility = 0.9 * volatility + 0.1 * dailyVol + volatility * volShock * 0.2;
    volatility = Math.max(dailyVol * 0.3, Math.min(dailyVol * 3, volatility));

    // Fat-tailed returns (Student-t with df=5)
    const z = rng.nextStudentT(5);
    let dailyReturn = dailyMean + volatility * z * 0.7;

    // Add momentum/trend component
    if (i > 0) {
      const recentMomentum = returns.slice(-5).reduce((a, b) => a + b, 0);
      dailyReturn += recentMomentum * 0.02; // Slight trend following
    }

    // Drawdown dynamics - larger losses when already in drawdown
    if (cumReturn < 0.95 && dailyReturn < 0) {
      dailyReturn *= 1 + drawdownIntensity;
    }

    // Clamp extreme values
    dailyReturn = Math.max(-0.08, Math.min(0.08, dailyReturn));

    returns.push(dailyReturn);
    cumReturn *= (1 + dailyReturn);
  }

  return returns;
}

/**
 * Generate benchmark (S&P 500-like) returns
 */
function generateBenchmarkReturns(days: number, seed: number): number[] {
  return generateDailyReturns({
    days,
    annualReturn: 0.10, // ~10% annual
    annualVol: 0.16, // ~16% vol
    drawdownIntensity: 0.3,
    seed,
  });
}

/**
 * Convert daily returns to monthly returns
 */
function calculateMonthlyReturns(
  dailyReturns: number[],
  startDate: string
): Record<number, Record<number, number>> {
  const monthly: Record<number, Record<number, number>> = {};
  const start = new Date(startDate);

  let monthlyReturn = 1;
  let currentMonth = start.getMonth() + 1;
  let currentYear = start.getFullYear();

  for (let i = 0; i < dailyReturns.length; i++) {
    const date = new Date(start);
    date.setDate(date.getDate() + i);
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    if (month !== currentMonth || year !== currentYear) {
      // Store previous month
      if (!monthly[currentYear]) monthly[currentYear] = {};
      monthly[currentYear][currentMonth] = monthlyReturn - 1;

      // Reset for new month
      monthlyReturn = 1;
      currentMonth = month;
      currentYear = year;
    }

    monthlyReturn *= (1 + dailyReturns[i]);
  }

  // Don't forget last month
  if (!monthly[currentYear]) monthly[currentYear] = {};
  monthly[currentYear][currentMonth] = monthlyReturn - 1;

  return monthly;
}

/**
 * Calculate rolling statistics
 */
function calculateRollingStats(dailyReturns: number[], window: number = 126): {
  sharpe: number[];
  sortino: number[];
  volatility: number[];
  beta: number[];
} {
  const sharpe: number[] = [];
  const sortino: number[] = [];
  const volatility: number[] = [];
  const beta: number[] = [];

  for (let i = window; i < dailyReturns.length; i++) {
    const windowReturns = dailyReturns.slice(i - window, i);
    const mean = windowReturns.reduce((a, b) => a + b, 0) / window;
    const variance = windowReturns.reduce((a, b) => a + (b - mean) ** 2, 0) / window;
    const std = Math.sqrt(variance);

    const downside = windowReturns.filter(r => r < 0);
    const downsideVariance = downside.length > 0
      ? downside.reduce((a, b) => a + b ** 2, 0) / downside.length
      : variance;
    const downsideStd = Math.sqrt(downsideVariance);

    const annualizedReturn = mean * 252;
    const annualizedVol = std * Math.sqrt(252);
    const annualizedDownsideVol = downsideStd * Math.sqrt(252);

    sharpe.push(annualizedVol > 0 ? annualizedReturn / annualizedVol : 0);
    sortino.push(annualizedDownsideVol > 0 ? annualizedReturn / annualizedDownsideVol : 0);
    volatility.push(annualizedVol);
    beta.push(0.5 + Math.random() * 0.5); // Simplified beta
  }

  return { sharpe, sortino, volatility, beta };
}

/**
 * Calculate underwater (drawdown) series
 */
function calculateUnderwater(dailyReturns: number[]): number[] {
  const underwater: number[] = [];
  let cumulative = 1;
  let peak = 1;

  for (const ret of dailyReturns) {
    cumulative *= (1 + ret);
    peak = Math.max(peak, cumulative);
    underwater.push(cumulative / peak - 1);
  }

  return underwater;
}

// ============================================================================
// Demo Strategies
// ============================================================================

export interface DemoStrategy {
  id: string;
  name: string;
  description: string;
  category: 'momentum' | 'mean_reversion' | 'factor' | 'ml';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  tearSheet: TearSheetData;
  code: string;
  highlight?: string; // Why this strategy is notable
}

const START_DATE = '2023-01-03';
const TRADING_DAYS = 504; // ~2 years

// Strategy 1: Momentum Alpha (Star Performer)
const momentumReturns = generateDailyReturns({
  days: TRADING_DAYS,
  annualReturn: 0.24,
  annualVol: 0.14,
  drawdownIntensity: 0.2,
  seed: 42,
});

const momentumBenchmark = generateBenchmarkReturns(TRADING_DAYS, 100);
const momentumRolling = calculateRollingStats(momentumReturns);
const momentumUnderwater = calculateUnderwater(momentumReturns);
const momentumMonthly = calculateMonthlyReturns(momentumReturns, START_DATE);

export const MOMENTUM_ALPHA: DemoStrategy = {
  id: 'demo-momentum-alpha',
  name: 'Momentum Alpha',
  description: 'Trend-following strategy using 12-month momentum with 1-month skip to avoid short-term reversals',
  category: 'momentum',
  difficulty: 'intermediate',
  highlight: 'Best risk-adjusted returns in demo portfolio',
  tearSheet: {
    basic: {
      total_return: momentumReturns.reduce((cum, r) => cum * (1 + r), 1) - 1,
      cagr: 0.24,
      sharpe_ratio: 1.85,
      sortino_ratio: 2.4,
      calmar_ratio: 2.18,
      max_drawdown: Math.min(...momentumUnderwater),
      win_rate: 0.58,
      profit_factor: 1.72,
    },
    advanced: {
      deflated_sharpe_ratio: 1.42,
      information_ratio: 0.85,
      omega_ratio: 1.45,
      tail_ratio: 1.15,
      treynor_ratio: 0.18,
      beta: 0.72,
      alpha: 0.14,
    },
    tail_risk: {
      skewness: 0.15,
      kurtosis: 3.8,
      var_95: -0.018,
      cvar_95: -0.028,
    },
    rolling: momentumRolling,
    drawdown: {
      underwater: momentumUnderwater,
      max_duration_days: 42,
      recovery_factor: 2.8,
    },
    monthly_returns: momentumMonthly,
    metadata: {
      start_date: START_DATE,
      end_date: '2024-12-31',
      trading_days: TRADING_DAYS,
      trial_count: 1,
    },
    returns: momentumReturns,
    benchmark_returns: momentumBenchmark,
  },
  code: `# Momentum Alpha Strategy
# Uses 12-month momentum with 1-month skip to capture intermediate trends

import pandas as pd
import numpy as np

def calculate_momentum(prices: pd.Series, lookback: int = 252, skip: int = 21) -> pd.Series:
    """Calculate momentum excluding recent month (reversal effect)."""
    total_return = prices.pct_change(lookback)
    recent_return = prices.pct_change(skip)
    return total_return - recent_return

def generate_signals(data: pd.DataFrame) -> pd.Series:
    """Generate long signals for top momentum quintile."""
    momentum = calculate_momentum(data['close'])
    signals = pd.Series(0.0, index=data.index)
    signals[momentum > momentum.quantile(0.8)] = 1.0
    return signals

def apply_risk_management(signals: pd.Series, data: pd.DataFrame) -> pd.Series:
    """Apply position sizing based on volatility."""
    vol = data['close'].pct_change().rolling(21).std() * np.sqrt(252)
    target_vol = 0.15
    position_size = (target_vol / vol).clip(0.5, 2.0)
    return signals * position_size
`,
};

// Strategy 2: Mean Reversion RSI (Solid Performer)
const meanRevReturns = generateDailyReturns({
  days: TRADING_DAYS,
  annualReturn: 0.14,
  annualVol: 0.18,
  drawdownIntensity: 0.35,
  seed: 123,
});

const meanRevBenchmark = generateBenchmarkReturns(TRADING_DAYS, 200);
const meanRevRolling = calculateRollingStats(meanRevReturns);
const meanRevUnderwater = calculateUnderwater(meanRevReturns);
const meanRevMonthly = calculateMonthlyReturns(meanRevReturns, START_DATE);

export const MEAN_REVERSION_RSI: DemoStrategy = {
  id: 'demo-mean-reversion-rsi',
  name: 'Mean Reversion RSI',
  description: 'Classic RSI strategy that buys oversold conditions and sells overbought',
  category: 'mean_reversion',
  difficulty: 'beginner',
  highlight: 'Great starter strategy with straightforward logic',
  tearSheet: {
    basic: {
      total_return: meanRevReturns.reduce((cum, r) => cum * (1 + r), 1) - 1,
      cagr: 0.14,
      sharpe_ratio: 1.2,
      sortino_ratio: 1.5,
      calmar_ratio: 0.78,
      max_drawdown: Math.min(...meanRevUnderwater),
      win_rate: 0.54,
      profit_factor: 1.35,
    },
    advanced: {
      deflated_sharpe_ratio: 0.95,
      information_ratio: 0.42,
      omega_ratio: 1.22,
      tail_ratio: 0.92,
      treynor_ratio: 0.11,
      beta: 0.85,
      alpha: 0.06,
    },
    tail_risk: {
      skewness: -0.25,
      kurtosis: 4.2,
      var_95: -0.024,
      cvar_95: -0.038,
    },
    rolling: meanRevRolling,
    drawdown: {
      underwater: meanRevUnderwater,
      max_duration_days: 68,
      recovery_factor: 1.6,
    },
    monthly_returns: meanRevMonthly,
    metadata: {
      start_date: START_DATE,
      end_date: '2024-12-31',
      trading_days: TRADING_DAYS,
      trial_count: 1,
    },
    returns: meanRevReturns,
    benchmark_returns: meanRevBenchmark,
  },
  code: `# Mean Reversion RSI Strategy
# Buys oversold, sells overbought using Relative Strength Index

import pandas as pd
import numpy as np

def calculate_rsi(prices: pd.Series, period: int = 14) -> pd.Series:
    """Calculate Relative Strength Index."""
    delta = prices.diff()
    gain = delta.where(delta > 0, 0).rolling(period).mean()
    loss = (-delta.where(delta < 0, 0)).rolling(period).mean()
    rs = gain / loss
    return 100 - (100 / (1 + rs))

def generate_signals(data: pd.DataFrame) -> pd.Series:
    """Buy oversold (<30), sell overbought (>70)."""
    rsi = calculate_rsi(data['close'])
    signals = pd.Series(0.0, index=data.index)
    signals[rsi < 30] = 1.0   # Long when oversold
    signals[rsi > 70] = -1.0  # Short when overbought
    return signals

def calculate_position(signals: pd.Series, data: pd.DataFrame) -> pd.Series:
    """Scale position by RSI extremity."""
    rsi = calculate_rsi(data['close'])
    scale = np.where(signals > 0, (30 - rsi) / 30,
                     np.where(signals < 0, (rsi - 70) / 30, 0))
    return signals * np.clip(scale, 0.5, 1.5)
`,
};

// Strategy 3: Sector Rotation (Moderate)
const sectorReturns = generateDailyReturns({
  days: TRADING_DAYS,
  annualReturn: 0.08,
  annualVol: 0.22,
  drawdownIntensity: 0.5,
  seed: 456,
});

const sectorBenchmark = generateBenchmarkReturns(TRADING_DAYS, 300);
const sectorRolling = calculateRollingStats(sectorReturns);
const sectorUnderwater = calculateUnderwater(sectorReturns);
const sectorMonthly = calculateMonthlyReturns(sectorReturns, START_DATE);

export const SECTOR_ROTATION: DemoStrategy = {
  id: 'demo-sector-rotation',
  name: 'Sector Rotation',
  description: 'Rotates between sector ETFs based on relative strength',
  category: 'factor',
  difficulty: 'intermediate',
  highlight: 'Shows market-like returns with sector timing',
  tearSheet: {
    basic: {
      total_return: sectorReturns.reduce((cum, r) => cum * (1 + r), 1) - 1,
      cagr: 0.08,
      sharpe_ratio: 0.75,
      sortino_ratio: 0.92,
      calmar_ratio: 0.29,
      max_drawdown: Math.min(...sectorUnderwater),
      win_rate: 0.51,
      profit_factor: 1.08,
    },
    advanced: {
      deflated_sharpe_ratio: 0.58,
      information_ratio: 0.15,
      omega_ratio: 1.08,
      tail_ratio: 0.78,
      treynor_ratio: 0.06,
      beta: 1.05,
      alpha: -0.02,
    },
    tail_risk: {
      skewness: -0.45,
      kurtosis: 4.8,
      var_95: -0.032,
      cvar_95: -0.052,
    },
    rolling: sectorRolling,
    drawdown: {
      underwater: sectorUnderwater,
      max_duration_days: 125,
      recovery_factor: 0.85,
    },
    monthly_returns: sectorMonthly,
    metadata: {
      start_date: START_DATE,
      end_date: '2024-12-31',
      trading_days: TRADING_DAYS,
      trial_count: 1,
    },
    returns: sectorReturns,
    benchmark_returns: sectorBenchmark,
  },
  code: `# Sector Rotation Strategy
# Allocates to top-performing sectors based on relative strength

import pandas as pd
import numpy as np

SECTORS = ['XLK', 'XLF', 'XLE', 'XLV', 'XLI', 'XLC', 'XLY', 'XLP', 'XLRE', 'XLU', 'XLB']

def calculate_relative_strength(prices: pd.DataFrame, lookback: int = 63) -> pd.DataFrame:
    """Calculate relative strength vs SPY for each sector."""
    spy_return = prices['SPY'].pct_change(lookback)
    rs = pd.DataFrame()
    for sector in SECTORS:
        sector_return = prices[sector].pct_change(lookback)
        rs[sector] = sector_return - spy_return
    return rs

def generate_signals(data: pd.DataFrame) -> pd.DataFrame:
    """Equal-weight top 3 sectors by relative strength."""
    rs = calculate_relative_strength(data)
    signals = pd.DataFrame(0.0, index=data.index, columns=SECTORS)

    for date in rs.index:
        top_3 = rs.loc[date].nlargest(3).index
        signals.loc[date, top_3] = 1/3

    return signals
`,
};

// Strategy 4: ML Overfit Example (Cautionary)
const mlOverfitReturns = generateDailyReturns({
  days: TRADING_DAYS,
  annualReturn: 0.03,
  annualVol: 0.35,
  drawdownIntensity: 0.8,
  seed: 789,
});

const mlOverfitBenchmark = generateBenchmarkReturns(TRADING_DAYS, 400);
const mlOverfitRolling = calculateRollingStats(mlOverfitReturns);
const mlOverfitUnderwater = calculateUnderwater(mlOverfitReturns);
const mlOverfitMonthly = calculateMonthlyReturns(mlOverfitReturns, START_DATE);

export const ML_OVERFIT_EXAMPLE: DemoStrategy = {
  id: 'demo-ml-overfit',
  name: 'ML Overfit Example',
  description: 'Demonstrates what happens when a model overfits to in-sample data',
  category: 'ml',
  difficulty: 'advanced',
  highlight: 'Educational: Shows importance of Deflated Sharpe Ratio',
  tearSheet: {
    basic: {
      total_return: mlOverfitReturns.reduce((cum, r) => cum * (1 + r), 1) - 1,
      cagr: 0.03,
      sharpe_ratio: 0.3,
      sortino_ratio: 0.35,
      calmar_ratio: 0.07,
      max_drawdown: Math.min(...mlOverfitUnderwater),
      win_rate: 0.48,
      profit_factor: 0.92,
    },
    advanced: {
      deflated_sharpe_ratio: 0.08,  // Very low - red flag!
      information_ratio: -0.15,
      omega_ratio: 0.95,
      tail_ratio: 0.65,
      treynor_ratio: 0.02,
      beta: 1.35,
      alpha: -0.08,
    },
    tail_risk: {
      skewness: -0.85,
      kurtosis: 6.2,
      var_95: -0.052,
      cvar_95: -0.085,
    },
    rolling: mlOverfitRolling,
    drawdown: {
      underwater: mlOverfitUnderwater,
      max_duration_days: 185,
      recovery_factor: 0.35,
    },
    monthly_returns: mlOverfitMonthly,
    metadata: {
      start_date: START_DATE,
      end_date: '2024-12-31',
      trading_days: TRADING_DAYS,
      trial_count: 247, // High trial count = more overfitting
    },
    returns: mlOverfitReturns,
    benchmark_returns: mlOverfitBenchmark,
  },
  code: `# ML Overfit Example
# WARNING: This strategy demonstrates overfitting pitfalls

import pandas as pd
import numpy as np
from sklearn.ensemble import RandomForestClassifier

def generate_features(data: pd.DataFrame) -> pd.DataFrame:
    """Generate excessive features (overfitting risk)."""
    features = pd.DataFrame(index=data.index)

    # Too many technical indicators
    for period in [5, 10, 20, 50, 100, 200]:
        features[f'sma_{period}'] = data['close'].rolling(period).mean()
        features[f'vol_{period}'] = data['close'].pct_change().rolling(period).std()
        features[f'rsi_{period}'] = calculate_rsi(data['close'], period)

    # Lagged features (look-ahead bias risk)
    for lag in range(1, 21):
        features[f'return_lag_{lag}'] = data['close'].pct_change().shift(lag)

    return features  # 60+ features for ~500 samples = overfitting

def train_model(X: pd.DataFrame, y: pd.Series):
    """Train overfit model (too complex for data size)."""
    model = RandomForestClassifier(
        n_estimators=500,  # Too many trees
        max_depth=None,    # No depth limit
        min_samples_leaf=1 # Allows memorization
    )
    model.fit(X, y)
    return model

# Result: 95% accuracy in-sample, 48% out-of-sample
# DSR correctly identifies this as unreliable
`,
};

export const DEMO_STRATEGIES: DemoStrategy[] = [
  MOMENTUM_ALPHA,
  MEAN_REVERSION_RSI,
  SECTOR_ROTATION,
  ML_OVERFIT_EXAMPLE,
];

// ============================================================================
// REMOVED (2026-08): DEMO_COMPETITION, DEMO_LEADERBOARD, tier helpers.
// They rendered fabricated user rankings and a fake "Weekly Alpha Challenge"
// on the dashboard home, contradicting the real Competitions/Strategies pages.
// Real implementation plan: docs/features/arena_competitions_rebuild.md
// ============================================================================
